import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { getErrorMessage, getErrorStatus } from '@/lib/errors';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { errorMessage?: string };
    mutationMeta: { errorMessage?: string; skipGlobalErrorToast?: boolean };
  }
}

const MAX_RETRIES = 2;
const NON_RETRYABLE_STATUSES = new Set([400, 401, 403, 404, 409, 422]);

export function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = getErrorStatus(error);
  if (status !== undefined && NON_RETRYABLE_STATUSES.has(status)) return false;
  return failureCount < MAX_RETRIES;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Only surface background refetch failures; initial load errors are
        // rendered inline by the page through <ErrorState />.
        if (query.state.data !== undefined) {
          toast.error(query.meta?.errorMessage ?? getErrorMessage(error));
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.skipGlobalErrorToast) return;
        toast.error(mutation.meta?.errorMessage ?? getErrorMessage(error));
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetry,
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: false,
      },
    },
  });
}
